import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { TextField, Checkbox } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Table, Button, Input, InputGroup, InputGroupText, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Nav, NavItem, NavLink, TabContent, TabPane, Progress, UncontrolledTooltip } from 'reactstrap';

import '../Admin.css';
import Plans from './Plans';
import Providers from './Providers';
import OverviewTab from './OverviewTab';
import CreditManagement from './CreditManagement';
import CallsAnalytics from './CallsAnalytics';
import DateFilter from './DateFilter';
import BulkActions from '../../../components/BulkActions';
import ExportButton from '../../../components/ExportButton';
import SubDetailsSidebar from './SubDetailsSidebar';
import { getAdminSubscriptions, getAllSubscriptionsForExport, getSubscriptionStats, getPayments, getRevenueAnalytics, getMRRAnalytics, getPlansOverview, getBillingOverview, getAgencyPlanPerformance, getAdminPlans, cancelSubscription, reactivateSubscription, applyCredit, bulkCancelSubscriptions, bulkReactivateSubscriptions, bulkApplyCredits, getAdminWorkspaces } from '../../../store/Admin';

const BillingManagement = ({ accessType, workspaceId }) => {
  const dispatch = useDispatch();
  const { adminSubscriptions, subscriptionStats, payments, revenueAnalytics, mrrAnalytics, plansOverview, subscriptionsPagination, paymentsPagination, adminWorkspaces, workspaceSummary, loading } = useSelector(state => state['Admin']);
  const { currentWorkspace } = useSelector(state => state['Settings'] || {});

  // Debug logging for accessType
  console.log('BillingManagement - AccessType Check:', {
    propAccessType: accessType,
    localStorageAccessType: localStorage.getItem('accessType'),
    finalAccessType: (accessType || localStorage.getItem('accessType')),
    isAgency: (accessType || localStorage.getItem('accessType')) === 'agency'
  });

  // Helper function to check if user is agency
  const isAgencyUser = () => {
    const userAccessType = accessType || localStorage.getItem('accessType');
    return userAccessType === 'agency';
  };

  const [activeTab, setActiveTab] = useState('analytics');
  const [formData, setFormData] = useState({});
  const [modalType, setModalType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);

  // Payment specific states
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [paymentCurrentPage, setPaymentCurrentPage] = useState(1);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');

  // Analytics specific states
  const [overviewDateRange, setOverviewDateRange] = useState('7d');
  const [analyticsDateRange, setAnalyticsDateRange] = useState('30d');
  const [overviewDateFilter, setOverviewDateFilter] = useState({
    start: moment().subtract(7, 'days').startOf('day').toDate(),
    end: moment().endOf('day').toDate()
  })

  // Handle date change for Overview tab
  const handleOverviewDateChange = (dateRange, rangeType) => {
    setOverviewDateFilter(dateRange);
    setOverviewDateRange(rangeType);

    // Trigger analytics updates with new date range
    dispatch(getRevenueAnalytics({ period: rangeType, startDate: dateRange.start, endDate: dateRange.end }));
    dispatch(getMRRAnalytics({ startDate: dateRange.start, endDate: dateRange.end }));
    dispatch(getSubscriptionStats({ startDate: dateRange.start, endDate: dateRange.end }));
    dispatch(getPlansOverview({ startDate: dateRange.start, endDate: dateRange.end }));
  }
  const [ltvAnalytics, setLtvAnalytics] = useState(null);
  const [churnAnalytics, setChurnAnalytics] = useState(null);

  // Plan assignment states
  const [assigningPlan, setAssigningPlan] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);
  const [selectAllWorkspaces, setSelectAllWorkspaces] = useState(false);
  const [selectedPlanToAssign, setSelectedPlanToAssign] = useState(null);
  const [assignmentProgress, setAssignmentProgress] = useState({ current: 0, total: 0, status: '' });


  // Prevent agency users from accessing restricted tabs
  useEffect(() => {
    if (isAgencyUser()) {
      if (activeTab === 'credits') {
        console.log(`Agency user tried to access credits tab, redirecting to analytics`);
        setActiveTab('analytics');
      }
      // Plans tab is now always accessible for agency users
    }
  }, [activeTab, accessType]);

  // Function to handle Analytics export
  const handleAnalyticsExport = () => {
    const dateRangeText = analyticsDateRange === '7d' ? 'Last 7 days' :
      analyticsDateRange === '30d' ? 'Last 30 days' :
        analyticsDateRange === '3m' ? 'Last 3 months' :
          analyticsDateRange === '6m' ? 'Last 6 months' :
            analyticsDateRange === '12m' ? 'Last 12 months' : 'Custom';

    const today = moment().format('YYYY-MM-DD');
    const reportDate = moment().format('MMMM DD, YYYY [at] HH:mm:ss');

    // Build the report content
    let reportContent = `BILLING & REVENUE ANALYTICS REPORT\n`;
    reportContent += `${'='.repeat(50)}\n`;
    reportContent += `Generated: ${reportDate}\n`;
    reportContent += `Period: ${dateRangeText}\n`;
    reportContent += `${'='.repeat(50)}\n\n`;

    // Executive Summary
    reportContent += `EXECUTIVE SUMMARY\n`;
    reportContent += `${'-'.repeat(30)}\n`;
    reportContent += `Total Revenue: $${(revenueAnalytics?.totalRevenue || 0).toLocaleString()}\n`;
    reportContent += `Active Subscriptions: ${subscriptionStats?.activeCount || 0}\n`;
    reportContent += `Monthly Recurring Revenue: $${(mrrAnalytics?.currentMRR || 0).toLocaleString()}\n`;
    reportContent += `Churn Rate: ${(subscriptionStats?.churnRate || 0).toFixed(2)}%\n`;
    reportContent += `Average Subscription Value: $${(subscriptionStats?.averageValue || 0).toLocaleString()}\n\n`;

    // Revenue Breakdown
    reportContent += `REVENUE BREAKDOWN\n`;
    reportContent += `${'-'.repeat(30)}\n`;
    if (revenueAnalytics?.breakdown) {
      reportContent += `Subscription Revenue: $${(revenueAnalytics.breakdown.subscription || 0).toLocaleString()}\n`;
      reportContent += `Credits Revenue: $${(revenueAnalytics.breakdown.credits || 0).toLocaleString()}\n`;
      reportContent += `Overage Charges: $${(revenueAnalytics.breakdown.overage || 0).toLocaleString()}\n`;
    }
    reportContent += `\n`;

    // Subscription Metrics
    reportContent += `SUBSCRIPTION METRICS\n`;
    reportContent += `${'-'.repeat(30)}\n`;
    reportContent += `New Subscriptions: ${subscriptionStats?.newCount || 0}\n`;
    reportContent += `Cancelled Subscriptions: ${subscriptionStats?.cancelledCount || 0}\n`;
    reportContent += `Growth Rate: ${(subscriptionStats?.subscriptionGrowth || 0).toFixed(2)}%\n`;
    reportContent += `Trial Conversions: ${subscriptionStats?.trialConversions || 0}\n`;
    reportContent += `\n`;

    // Top Performing Plans
    if (plansOverview && plansOverview.length > 0) {
      reportContent += `TOP PERFORMING PLANS\n`;
      reportContent += `${'-'.repeat(30)}\n`;
      plansOverview.slice(0, 5).forEach((plan, index) => {
        if (plan.subscriberCount > 0 || plan.revenue > 0) {
          reportContent += `${index + 1}. ${plan.name || 'Unknown Plan'}\n`;
          reportContent += `   Subscriptions: ${plan.subscriberCount || 0}\n`;
          reportContent += `   Revenue: $${(plan.revenue || 0).toLocaleString()}\n`;
          reportContent += `   Avg Value: $${(plan.avgValue || 0).toLocaleString()}\n\n`;
        }
      });
    }

    // Payment Analytics
    reportContent += `PAYMENT ANALYTICS\n`;
    reportContent += `${'-'.repeat(30)}\n`;
    const successfulPayments = payments?.filter(p => p.status === 'succeeded') || [];
    const failedPayments = payments?.filter(p => p.status === 'failed') || [];
    const totalPayments = payments?.length || 0;
    const successRate = totalPayments > 0 ? (successfulPayments.length / totalPayments * 100) : 0;

    reportContent += `Total Payments: ${totalPayments}\n`;
    reportContent += `Successful Payments: ${successfulPayments.length} ($${successfulPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()})\n`;
    reportContent += `Failed Payments: ${failedPayments.length} ($${failedPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()})\n`;
    reportContent += `Success Rate: ${successRate.toFixed(2)}%\n\n`;

    // MRR Trends
    reportContent += `MRR TRENDS\n`;
    reportContent += `${'-'.repeat(30)}\n`;
    reportContent += `Current MRR: $${(mrrAnalytics?.currentMRR || 0).toLocaleString()}\n`;
    reportContent += `MRR Growth: ${(mrrAnalytics?.mrrGrowth || 0).toFixed(2)}%\n`;
    reportContent += `New MRR: $${(mrrAnalytics?.newMRR || 0).toLocaleString()}\n`;
    reportContent += `Churned MRR: $${(mrrAnalytics?.churnedMRR || 0).toLocaleString()}\n`;
    reportContent += `Expansion MRR: $${(mrrAnalytics?.expansionMRR || 0).toLocaleString()}\n\n`;

    // Footer
    reportContent += `${'='.repeat(50)}\n`;
    reportContent += `END OF REPORT\n`;
    reportContent += `This report contains only non-empty, meaningful data.\n`;
    reportContent += `Generated by Billing & Revenue Analytics System\n`;

    // Create and download the file
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `billing_revenue_report_${today}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Show success message (assuming you have a notification system)
    // console.log('Report exported successfully!');
  };

  // Enhanced payments states for workspace features
  const [selectedWorkspaceFilter, setSelectedWorkspaceFilter] = useState('');
  const [groupByWorkspace, setGroupByWorkspace] = useState(false);

  useEffect(() => {
    // Load overview stats
    if (activeTab === 'overview') {
      console.log('📊 Loading Overview Tab Data...', {
        overviewDateRange,
        overviewDateFilter
      });
      // Use the same endpoint as Dashboard for consistency
      dispatch(getRevenueAnalytics({
        period: overviewDateRange,
        startDate: overviewDateFilter.start,
        endDate: overviewDateFilter.end
      }));
      dispatch(getMRRAnalytics({
        startDate: overviewDateFilter.start,
        endDate: overviewDateFilter.end
      }));
      dispatch(getSubscriptionStats({
        startDate: overviewDateFilter.start,
        endDate: overviewDateFilter.end
      }));
      dispatch(getPlansOverview({
        startDate: overviewDateFilter.start,
        endDate: overviewDateFilter.end
      }));
      // Also get agency-specific plan performance
      dispatch(getAgencyPlanPerformance());
    } else if (activeTab === 'subscriptions') {
      dispatch(getAdminSubscriptions({ page: currentPage, search: searchTerm }));
    } else if (activeTab === 'payments') {
      dispatch(getPayments({
        page: paymentCurrentPage,
        search: paymentSearchTerm,
        status: paymentStatusFilter,
        type: paymentTypeFilter,
        workspaceId: selectedWorkspaceFilter
      }));
    } else if (activeTab === 'analytics') {
      // Calculate date range for analytics
      let startDate, endDate = moment().endOf('day').toDate();
      switch (analyticsDateRange) {
        case '7d':
          startDate = moment().subtract(7, 'days').startOf('day').toDate();
          break;
        case '30d':
          startDate = moment().subtract(30, 'days').startOf('day').toDate();
          break;
        case '3m':
          startDate = moment().subtract(3, 'months').startOf('day').toDate();
          break;
        case '6m':
          startDate = moment().subtract(6, 'months').startOf('day').toDate();
          break;
        case '12m':
          startDate = moment().subtract(12, 'months').startOf('day').toDate();
          break;
        default:
          startDate = moment().subtract(30, 'days').startOf('day').toDate();
      }

      // Load all analytics data with date range
      // Use the same endpoints as Dashboard and Overview tab for consistency
      dispatch(getRevenueAnalytics({ period: analyticsDateRange, startDate, endDate }));
      dispatch(getSubscriptionStats({ startDate, endDate }));
      dispatch(getPlansOverview({ startDate, endDate }));
      // Get agency-specific plan performance for analytics tab
      dispatch(getAgencyPlanPerformance());
      // Load additional analytics through API calls
      loadChurnAnalytics();
      loadLTVAnalytics();
    } else if (activeTab === 'plans') {
      dispatch(getAdminPlans());
    }
  }, [activeTab, currentPage, searchTerm, paymentCurrentPage, paymentSearchTerm, paymentStatusFilter, paymentTypeFilter, selectedWorkspaceFilter, analyticsDateRange, overviewDateRange, overviewDateFilter]);

  const handleSubscriptionAction = async (subscriptionId, action, data = {}) => {
    if (!subscriptionId) {
      // console.error('No subscription ID provided for action:', action);
      return;
    }

    const filters = { page: currentPage, search: searchTerm };

    try {
      if (action === 'cancel') {
        await dispatch(cancelSubscription({ id: subscriptionId, data, filters }));
      } else if (action === 'reactivate') {
        await dispatch(reactivateSubscription({ id: subscriptionId, filters }));
      } else if (action === 'credit') {
        await dispatch(applyCredit({ id: subscriptionId, data, filters }));
        // Refresh subscriptions after applying credit
        dispatch(getAdminSubscriptions({ page: currentPage, search: searchTerm }));
      }
    } catch (error) {
      // console.error('Error performing subscription action:', error);
    }
  };

  const handleBulkSubscriptionAction = async (action, subscriptionIds, data = {}) => {
    const filters = { page: currentPage, search: searchTerm };

    switch (action) {
      case 'cancel':
        return dispatch(bulkCancelSubscriptions({
          subscriptionIds,
          reason: data.reason || 'Admin cancelled'
        }));
      case 'reactivate':
        return dispatch(bulkReactivateSubscriptions({
          subscriptionIds
        }));
      case 'apply-credit':
        return dispatch(bulkApplyCredits({
          subscriptionIds,
          amount: data.amount,
          reason: data.description || 'Admin credit adjustment'
        }));
      case 'bulk-email':
        return dispatch({ type: 'BULK_EMAIL_SUBSCRIPTIONS', payload: { subscriptionIds, subject: data['subject'], message: data['message'] } });
      default:
        // console.warn('Unknown bulk subscription action:', action);
    }

    // Clear selection after action
    setSelectedSubscriptions([]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedSubscriptions([]);
    else setSelectedSubscriptions(adminSubscriptions.map(sub => sub['_id']));
    setSelectAll(!selectAll);
  };

  const handleDeselectAll = () => {
    setSelectedSubscriptions([]);
    setSelectAll(false);
  };

  const handleSubscriptionSelect = (subscriptionId) => {
    setSelectedSubscriptions(prev => {
      const newSelected = prev.includes(subscriptionId)
        ? prev.filter(id => id !== subscriptionId)
        : [...prev, subscriptionId];

      setSelectAll(newSelected.length === adminSubscriptions.length);
      return newSelected;
    });
  };

  const subscriptionBulkActions = [
    { key: 'apply-credit', type: 'edit', label: 'Apply Credit', description: 'Apply credit to selected subscriptions', requiresConfirmation: true, fields: [{ key: 'amount', label: 'Credit Amount ($)', type: 'number', required: true, placeholder: '0.00' }, { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Credit reason...' }] },
    { key: 'reactivate', type: 'success', label: 'Reactivate Subscriptions', description: 'Reactivate selected cancelled subscriptions', requiresConfirmation: true, confirmationMessage: 'This will reactivate all selected cancelled subscriptions.', withProgress: true },
    { key: 'cancel', type: 'delete', label: 'Cancel Subscriptions', description: 'Cancel selected subscriptions', requiresConfirmation: true, confirmationMessage: 'This will cancel all selected subscriptions.', warning: 'Cancelled subscriptions will lose access at the end of their billing period.', withProgress: true },
    { key: 'bulk-email', type: 'email', label: 'Send Email', description: 'Send email to subscription owners', requiresConfirmation: true, fields: [{ key: 'subject', label: 'Subject', type: 'text', required: true, placeholder: 'Email subject' }, { key: 'message', label: 'Message', type: 'textarea', required: true, placeholder: 'Email message' }] }
  ];

  const openModal = (type, subscription = null) => {
    setModalType(type);
    setSelectedSubscription(subscription);
    setShowModal(true);
    setFormData({});
  };

  const getStatusBadge = (status, subscription) => {
    const statusColors = {
      active: 'success',
      canceled: 'danger',
      trialing: 'info',
      past_due: 'warning',
      incomplete: 'secondary'
    };

    // Check if subscription is set to cancel at period end
    if (subscription?.cancelAtPeriodEnd && status === 'active') {
      return (
        <span>
          <Badge color="warning">Canceling</Badge>
          <small className="text-muted ml-1">(at period end)</small>
        </span>
      );
    }

    return <Badge color={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      succeeded: 'success',
      failed: 'danger',
      pending: 'warning',
      processing: 'info',
      refunded: 'secondary',
      canceled: 'dark',
      requires_action: 'warning'
    };
    return <Badge color={statusColors[status] || 'secondary'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPaymentTypeBadge = (type) => {
    const typeColors = {
      subscription: 'primary',
      credits: 'warning',
      overage: 'danger',
      upgrade: 'info',
      refund: 'secondary'
    };
    return <Badge color={typeColors[type] || 'secondary'}>{type}</Badge>;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  // Helper function to format plan names for display
  const formatPlanName = (planName) => {
    if (!planName || planName === '-') {
      return 'Free';
    }

    // Parse technical plan names like "Enterprise_business_year_agency_68b0320f1cc00125188a9e73"
    const parts = planName.split('_');
    if (parts.length > 0) {
      // Capitalize the first part (plan name)
      let displayName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);

      // Add plan type if present (business, etc)
      if (parts.length >= 2 && parts[1]) {
        const planType = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
        displayName += ` (${planType})`;
      }

      // Add billing interval if present
      if (parts.length >= 3) {
        const interval = parts[2]; // month, year, etc
        if (interval === 'month') {
          displayName += ' - Monthly';
        } else if (interval === 'year') {
          displayName += ' - Yearly';
        }
      }

      return displayName;
    }

    return planName;
  };


  const loadChurnAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/billing/analytics/churn', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setChurnAnalytics(data.body);
      }
    } catch (error) {
      // console.error('Error loading churn analytics:', error);
    }
  };

  const loadLTVAnalytics = async () => {
    try {
      const response = await fetch('/api/admin/billing/analytics/ltv', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setLtvAnalytics(data.body);
      }
    } catch (error) {
      // console.error('Error loading LTV analytics:', error);
    }
  };

  const calculateTotalRevenue = () => {
    // Use the same calculation as Dashboard - from revenue analytics endpoint
    const totalRev = revenueAnalytics?.totalRevenue || 0;
    console.log('📊 Overview Tab - Data Check:', {
      totalRevenue: totalRev,
      revenueAnalytics: revenueAnalytics,
      plansOverview: plansOverview,
      subscriptionStats: subscriptionStats
    });
    return totalRev;
  };

  const calculateARPU = () => {
    // Use MRR from current month payments divided by active subscriptions
    const monthlyRevenue = revenueAnalytics?.planBasedSubscriptionValue || 0;
    const activeUsers = subscriptionStats?.activeCount || 1;
    const arpu = activeUsers > 0 ? (monthlyRevenue / activeUsers).toFixed(2) : '0.00';
    console.log('📊 Overview Tab - ARPU:', {
      monthlyRevenue,
      activeUsers,
      arpu,
      revenueAnalytics: revenueAnalytics,
      subscriptionStats: subscriptionStats
    });
    return arpu;
  };

  // Generate chart data from plansOverview
  const getPlanDistributionChartData = () => {
    console.log('📊 Chart Data Generation:', { plansOverview });

    if (!plansOverview?.plans || plansOverview.plans.length === 0) {
      console.log('❌ No plans data available');
      return null;
    }

    // Show all plans, not just those with active subscribers
    const plans = plansOverview.plans;

    console.log('📊 Plans for charts:', plans);

    // Always return data even if all values are 0
    return {
      labels: plans.map(p => p.displayName || p.name),
      data: plans.map(p => p.metrics?.activeSubscribers || 0),
      revenue: plans.map(p => p.metrics?.monthlyRevenue || 0),
      colors: [
        '#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
        '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#A78BFA', '#F472B6'
      ]
    };
  };

  const handleApplyPlanClick = (plan) => {
    setSelectedPlanToAssign(plan);
    setShowAssignPlanModal(true);
    setWorkspaceSearchTerm('');
    setSelectedWorkspaces([]);
    setSelectAllWorkspaces(false);
    loadAllWorkspaces(); // Load all workspaces initially
  };

  const loadAllWorkspaces = async () => {
    setLoadingWorkspaces(true);
    try {
      // Use Redux action to fetch workspaces like the Admin Workspaces component does
      dispatch(getAdminWorkspaces({ page: 1, limit: 1000, search: '', status: '' }));
    } catch (error) {
      console.error('Error loading workspaces via Redux:', error);
      setAvailableWorkspaces([]);
      setLoadingWorkspaces(false);
    }
  };

  // Update available workspaces when Redux state changes
  useEffect(() => {
    if (adminWorkspaces && Array.isArray(adminWorkspaces)) {
      console.log(`Redux state updated with ${adminWorkspaces.length} workspaces`, adminWorkspaces);
      setAvailableWorkspaces(adminWorkspaces);
    }
    setLoadingWorkspaces(false);
  }, [adminWorkspaces]);

  const searchWorkspaces = async (searchTerm) => {
    setLoadingWorkspaces(true);
    try {
      const response = await fetch(`/api/admin/workspaces?search=${searchTerm}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAvailableWorkspaces(data.body.workspaces || []);
      }
    } catch (error) {
      console.error('Error searching workspaces:', error);
      setAvailableWorkspaces([]);
    }
    setLoadingWorkspaces(false);
  };

  const handleWorkspaceSearch = (e) => {
    const term = e.target.value;
    setWorkspaceSearchTerm(term);
    if (term.length > 2) {
      searchWorkspaces(term);
    } else if (term.length === 0) {
      loadAllWorkspaces(); // Load all workspaces when search is cleared
    }
  };

  const handleWorkspaceSelection = (workspace, isSelected) => {
    if (isSelected) {
      setSelectedWorkspaces(prev => [...prev, workspace]);
    } else {
      setSelectedWorkspaces(prev => prev.filter(w => w._id !== workspace._id));
    }
  };

  const handleSelectAllWorkspaces = (isSelected) => {
    setSelectAllWorkspaces(isSelected);
    if (isSelected) {
      setSelectedWorkspaces([...availableWorkspaces]);
    } else {
      setSelectedWorkspaces([]);
    }
  };

  const handleAssignPlan = async () => {
    if (selectedWorkspaces.length === 0 || !selectedPlanToAssign) {
      return;
    }

    setShowAssignPlanModal(false);
    setShowProgressModal(true);
    setAssigningPlan(true);
    setAssignmentProgress({ current: 0, total: selectedWorkspaces.length, status: 'Starting...' });
    setAssignmentResults([]);

    const results = [];

    for (let i = 0; i < selectedWorkspaces.length; i++) {
      const workspace = selectedWorkspaces[i];
      setAssignmentProgress({
        current: i + 1,
        total: selectedWorkspaces.length,
        status: `Processing ${workspace.name}...`
      });

      try {
        const response = await fetch('/api/admin/billing/assign-plan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            workspaceId: workspace._id,
            planId: selectedPlanToAssign._id,
            createStripeSubscription: selectedPlanToAssign.type === 'subscription' && selectedPlanToAssign.price > 0
          })
        });

        console.log('Response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('HTTP Error:', response.status, errorText);
          results.push({
            workspace: workspace,
            success: false,
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: errorText
          });
          continue;
        }

        const data = await response.json();
        console.log('Assignment response for', workspace.name, data);

        results.push({
          workspace: workspace,
          success: data.success,
          message: data.message || (data.success ? 'Successfully assigned' : 'Failed to assign'),
          details: data.body
        });

      } catch (error) {
        console.error('Error assigning plan to workspace:', workspace.name, error);
        results.push({
          workspace: workspace,
          success: false,
          message: `Network error: ${error.message || 'Connection failed'}`,
          details: error.toString()
        });
      }
    }

    setAssignmentResults(results);
    setAssignmentProgress({
      current: selectedWorkspaces.length,
      total: selectedWorkspaces.length,
      status: 'Completed'
    });
    setAssigningPlan(false);

    // Refresh overview data
    dispatch(getPlansOverview());
    dispatch(getSubscriptionStats());
  };


  return (
    <>
      <div className='content-dashboard'>
        <h3>Billing & Subscription Management</h3>
        <p className="text-muted mb-3">
          {isAgencyUser()
            ? 'Manage sub-workspace subscriptions, payments, and revenue analytics for your agency'
            : 'Manage subscriptions, payments, and revenue analytics'
          }
        </p>
        <div className='tabs-area'>
          {/* Navigation Tabs */}
          <div className='tab-header'>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === 'analytics' ? 'active' : ''}
                  onClick={() => setActiveTab('analytics')}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'subscriptions' ? 'active' : ''}
                  onClick={() => setActiveTab('subscriptions')}
                >
                  Subscriptions
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'payments' ? 'active' : ''}
                  onClick={() => setActiveTab('payments')}
                >
                  Payments
                </NavLink>
              </NavItem>
              {/* Payment Plans Tab - For all users */}
              <NavItem>
                <NavLink
                  className={activeTab === 'plans' ? 'active' : ''}
                  onClick={() => setActiveTab('plans')}
                >
                  Plans
                </NavLink>
              </NavItem>
              {/* Calls Analytics Tab - For all users */}
              {!isAgencyUser() && (
              <NavItem>
                <NavLink
                  className={activeTab === 'calls-analytics' ? 'active' : ''}
                  onClick={() => setActiveTab('calls-analytics')}
                >
                  <i className="fa fa-phone-volume mr-2"></i>
                  Calls Analytics
                </NavLink>
              </NavItem>
               )}
              {/* Providers Tab - For all users */}
              {!isAgencyUser() && (
              <NavItem>
                <NavLink
                  className={activeTab === 'providers' ? 'active' : ''}
                  onClick={() => setActiveTab('providers')}
                >
                  Providers
                </NavLink>
              </NavItem>
              )}
              {/* Credit Management - For non-agency users */}
              {!isAgencyUser() && (
                <NavItem>
                  <NavLink
                    className={activeTab === 'credits' ? 'active' : ''}
                    onClick={() => setActiveTab('credits')}
                  >
                    Credit Management
                  </NavLink>
                </NavItem>
              )}
            </Nav>
          </div>
          <TabContent activeTab={activeTab}>
            {/* Overview Tab */}
            <TabPane tabId="overview">
              <OverviewTab
                dispatch={dispatch}
                mrrAnalytics={mrrAnalytics}
                revenueAnalytics={revenueAnalytics}
                subscriptionStats={subscriptionStats}
                plansOverview={plansOverview}
                dateFilter={overviewDateFilter}
                selectedRange={overviewDateRange}
                onDateChange={handleOverviewDateChange}
                getRevenueAnalytics={getRevenueAnalytics}
                getMRRAnalytics={getMRRAnalytics}
                getSubscriptionStats={getSubscriptionStats}
                getPlansOverview={getPlansOverview}
              />
            </TabPane>

            {/* Subscriptions Tab */}
            <TabPane tabId="subscriptions">
              <div className='content-dashboard'>
                <div className='title-area style-workshop'>
                  <h4>
                    {isAgencyUser()
                      ? 'Sub-Workspace Subscriptions'
                      : 'All Subscriptions'
                    }
                  </h4>
                </div>
                <div className="table-container filters-table">
                  {/* Search and Filters */}
                  <div className='header-table-filters advanced-table-filters'>
                    <div className='left-area'>
                      <div className='filter-box'>
                        <div className='searchbox'>
                          <TextField
                            fullWidth
                            hiddenLabel
                            size="small"
                            name='search'
                            variant="standard"
                            placeholder={isAgencyUser()
                              ? "Search sub-workspace subscriptions..."
                              : "Search subscriptions..."
                            }
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e['target']['value'])}
                          />
                          <i className='icon'>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                              <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                            </svg>
                          </i>
                        </div>
                      </div>
                    </div>
                    <div className='right-deals-filtes'>
                      <ExportButton
                        data={adminSubscriptions}
                        filename={isAgencyUser()
                          ? "sub-workspace-subscriptions"
                          : "subscriptions"
                        }
                        buttonSize="sm"
                        className="mr-2"
                        fetchAllData={async () => {
                          // Fetch all subscriptions for export
                          return new Promise((resolve) => {
                            dispatch({
                              type: 'GET_ALL_SUBSCRIPTIONS_FOR_EXPORT',
                              payload: { search: searchTerm },
                              callback: (data) => {
                                resolve(data || adminSubscriptions);
                              }
                            });
                            // Fallback timeout
                            setTimeout(() => resolve(adminSubscriptions), 5000);
                          });
                        }}
                        columns={[
                          { key: 'customerName', name: 'Customer Name' },
                          { key: 'customerEmail', name: 'Customer Email' },
                          { key: 'workspaceId', name: 'Workspace', formatter: (val) => val?.['name'] || 'N/A' },
                          { key: 'planName', name: 'Plan' },
                          { key: 'planInterval', name: 'Interval' },
                          { key: 'status', name: 'Status' },
                          { key: 'amount', name: 'Amount', formatter: (val) => `$${val?.toLocaleString() || 0}` },
                          { key: 'creditBalance', name: 'Credits', formatter: (val) => `$${((val || 0) / 100).toFixed(2)}` },
                          { key: 'nextBilling', name: 'Next Billing', formatter: (val) => val ? new Date(val).toLocaleDateString() : 'N/A' },
                          { key: 'createdAt', name: 'Created', formatter: (val) => new Date(val).toLocaleDateString() }
                        ]}
                      />
                    </div>
                  </div>
                  <div className='table-area'>
                    {loading ? (
                      <div className="text-center">
                        {isAgencyUser()
                          ? 'Loading sub-workspace subscriptions...'
                          : 'Loading subscriptions...'
                        }
                      </div>
                    ) : (
                      <>
                        {!isAgencyUser() && (
                          <BulkActions
                            selectedItems={selectedSubscriptions}
                            totalItems={adminSubscriptions.length}
                            onSelectAll={handleSelectAll}
                            onDeselectAll={handleDeselectAll}
                            actions={subscriptionBulkActions}
                            onAction={handleBulkSubscriptionAction}
                            isLoading={loading}
                          />
                        )}

                        <Table responsive hover>
                          <thead>
                            <tr>
                              {!isAgencyUser() && (
                                <th width="30">
                                  <Checkbox checked={selectAll} onChange={handleSelectAll} />
                                </th>
                              )}
                              <th>Customer</th>
                              <th>Workspace</th>
                              <th>Plan</th>
                              <th>Status</th>
                              <th>Amount</th>
                              {/* <th>Credits</th> */}
                              <th>Next Billing</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminSubscriptions.map(sub => (
                              <tr key={sub['_id']}>
                                {!isAgencyUser() && (
                                  <td>
                                    <Checkbox checked={selectedSubscriptions.includes(sub['_id'])} onChange={() => handleSubscriptionSelect(sub['_id'])} />
                                  </td>
                                )}
                                <td>
                                  <div>
                                    <strong>{sub['customerName'] || 'Unknown'}</strong>
                                    <br />
                                    <small className="text-muted">{sub['customerEmail'] || 'N/A'}</small>
                                  </div>
                                </td>
                                <td>
                                  <div>{sub['workspaceId']?.['name'] || 'N/A'}</div>
                                </td>
                                <td>
                                  <div>
                                    <Badge color="primary">{formatPlanName(sub['planName']) || 'Unknown'}</Badge>
                                    <br />
                                    <small className="text-muted">{sub['planInterval'] || ''}</small>
                                  </div>
                                </td>
                                <td>{getStatusBadge(sub['status'], sub)}</td>
                                <td>
                                  <strong>${(sub['amount'] || 0).toLocaleString()}</strong>
                                  {sub['planInterval'] && <small>/{sub['planInterval']}</small>}
                                </td>
                                <td>
                                  <Badge color={(sub?.['creditBalance'] || 0) > 0 ? 'success' : 'secondary'}>
                                    ${((sub?.['creditBalance'] || 0) / 100).toFixed(2)}
                                  </Badge>
                                </td>
                                <td>
                                  {sub['nextBilling'] ? new Date(sub['nextBilling']).toLocaleDateString() : 'N/A'}
                                </td>
                                <td>
                                  <div className='action-box'>
                                    <button className='action-btn' title="View Details" onClick={() => {
                                      setSelectedSubscription(sub);
                                      setShowSidebar(true);
                                    }}>
                                      <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43"></circle><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43"></path><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43"></path></svg></i>
                                    </button>
                                    {!isAgencyUser() && (
                                      <button className='action-btn text-success' title="Apply Credits" onClick={() => openModal('credit', sub)}>
                                        <i className="fa fa-dollar-sign"></i>
                                      </button>
                                    )}
                                    {!isAgencyUser() && (
                                      sub['status'] === 'active' && !sub['cancelAtPeriodEnd'] ? (
                                        <button className='action-btn text-danger' title="Cancel Subscription" onClick={() => openModal('cancel', sub)}>
                                          <i className="fa fa-times"></i>
                                        </button>
                                      ) : (sub['status'] === 'canceled' || sub['cancelAtPeriodEnd']) ? (
                                        <button className='action-btn text-primary' title="Reactivate Subscription" onClick={() => handleSubscriptionAction(sub['_id'], 'reactivate')}>
                                          <i className="fa fa-check"></i>
                                        </button>
                                      ) : null
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        {/* Pagination */}
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <PaginationItem disabled={currentPage === 1}>
                              <PaginationLink
                                previous
                                onClick={() => setCurrentPage(currentPage - 1)}
                              />
                            </PaginationItem>

                            {[...Array(Math.min(5, subscriptionsPagination?.pages || 1))].map((_, i) => {
                              const page = i + 1;
                              return (
                                <PaginationItem key={page} active={page === currentPage}>
                                  <PaginationLink onClick={() => setCurrentPage(page)}>
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            })}

                            <PaginationItem disabled={currentPage === (subscriptionsPagination?.pages || 1)}>
                              <PaginationLink
                                next
                                onClick={() => setCurrentPage(currentPage + 1)}
                              />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Payments Tab */}
            <TabPane tabId="payments">
              <div className='content-dashboard'>
                <div className='title-area style-workshop'>
                  <h4>Payment History</h4>
                  <p className="text-muted">View and manage all payment transactions across all workspaces</p>
                </div>

                {/* Workspace Summary Cards */}
                {workspaceSummary && workspaceSummary.overview && (
                  <div className="mb-4">
                    <div className='counter-sections mb-3'>
                      <div className='counter-box style-two'>
                        <div className='counter-start' >
                          <div className='left-text-area'>
                            <h5>Total Workspaces</h5>
                            <div className='CountUp-box'>
                              <h3 className="" style={{ color: '#9333ea' }}>{workspaceSummary.overview.totalWorkspaces}</h3>
                            </div>
                            <small className="text-success">
                              {workspaceSummary.overview.workspacesWithPayments} with payments
                            </small>
                          </div>
                          <div className='right-icon'>
                            <span className='icon'><i className="fa fa-building fa-2x text-primary "></i></span>
                          </div>
                        </div>
                      </div>

                      <div className='counter-box style-two'>
                        <div className='counter-start' >
                          <div className='left-text-area'>
                            <h5>Total Revenue</h5>
                            <div className='CountUp-box'>
                              <h3 className="text-success">${(calculateTotalRevenue()).toLocaleString()}</h3>
                            </div>
                            <small className='text-success'>
                              <i className='fa fa-arrow-up'></i> All time
                            </small>
                          </div>
                          <div className='right-icon'>
                            <span className='icon'><i className='fa fa-dollar-sign text-success'></i></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="table-container filters-table">
                  {/* Filters */}
                  <div className='header-table-filters advanced-table-filters payment-filters-responsive' style={{ flexWrap: 'nowrap' }}>
                    <div className='left-area d-flex align-items-center' style={{ flex: '1', minWidth: 0 }}>
                      <div className='filter-box d-flex gap-2' style={{ flexWrap: 'nowrap' }}>

                        {/* Status Filter */}
                        <select
                          className="form-control form-control-sm"
                          style={{ width: '140px', flexShrink: 0 }}
                          value={paymentStatusFilter}
                          onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        >
                          <option value="">All Status</option>
                          <option value="succeeded">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="failed">Failed</option>
                          <option value="canceled">Canceled</option>
                        </select>

                        {/* Type Filter */}
                        <select
                          className="form-control form-control-sm"
                          style={{ width: '140px', flexShrink: 0 }}
                          value={paymentTypeFilter}
                          onChange={(e) => setPaymentTypeFilter(e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="subscription">Subscription</option>
                          <option value="credits">Credits</option>
                          <option value="overage">Overage</option>
                        </select>

                        {/* Workspace Filter */}
                        <select
                          className="form-control form-control-sm"
                          style={{ width: '170px', flexShrink: 0 }}
                          value={selectedWorkspaceFilter}
                          onChange={(e) => setSelectedWorkspaceFilter(e.target.value)}
                        >
                          <option value="">All Workspaces</option>
                          {workspaceSummary && workspaceSummary.workspaceBreakdown &&
                            workspaceSummary.workspaceBreakdown.map(workspace => (
                              <option key={workspace.workspace._id} value={workspace.workspace._id}>
                                {workspace.workspace.name} ({workspace.paymentStats.paymentCount} payments)
                              </option>
                            ))
                          }
                        </select>


                      </div>
                    </div>
                    <div className='right-deals-filtes d-flex align-items-center justify-content-end' style={{ flexShrink: 0, gap: '12px', marginLeft: '12px' }}>
                      {/* Group by Workspace Toggle */}
                      <button
                        size="sm"
                        className='btn-style-new'
                        color={groupByWorkspace ? "success" : "outline-secondary"}
                        onClick={() => setGroupByWorkspace(!groupByWorkspace)}
                        style={{ minWidth: '140px', whiteSpace: 'nowrap' }}
                      >
                        <i className={`fa ${groupByWorkspace ? 'fa-list' : 'fa-layer-group'} mr-2`}></i>
                        {groupByWorkspace ? 'Ungroup' : 'Group by Workspace'}
                      </button>
                      <button
                        size="sm"
                        color="primary"
                        className="btn-style-border"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setPaymentCurrentPage(1);
                          dispatch(getPayments({
                            page: 1,
                            search: '',
                            status: '',
                            type: '',
                            workspaceId: ''
                          }));
                          setPaymentSearchTerm('');
                          setPaymentStatusFilter('');
                          setPaymentTypeFilter('');
                          setSelectedWorkspaceFilter('');
                        }}
                      >
                        <i className="fa fa-undo mr-1"></i>Refresh
                      </button>
                    </div>
                  </div>

                  <div className='table-area'>
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading payments...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading payment history...</p>
                      </div>
                    ) : payments && payments.length > 0 ? (
                      groupByWorkspace ? (
                        // Grouped by Workspace View
                        <div>
                          {(() => {
                            // Group payments by workspace
                            const groupedPayments = payments.reduce((groups, payment) => {
                              const workspaceId = payment.workspaceId?._id || 'unknown';
                              const workspaceName = payment.workspaceId?.name || 'Unknown Workspace';
                              if (!groups[workspaceId]) {
                                groups[workspaceId] = {
                                  name: workspaceName,
                                  payments: []
                                };
                              }
                              groups[workspaceId].payments.push(payment);
                              return groups;
                            }, {});

                            return Object.entries(groupedPayments).map(([workspaceId, workspaceGroup]) => (
                              <div key={workspaceId} className="mb-4">
                                <Card>
                                  <CardHeader className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <h5 className="mb-0">
                                        <i className="fa fa-building mr-2 text-primary"></i>
                                        {workspaceGroup.name}
                                      </h5>
                                      <Badge color="primary" pill>
                                        {workspaceGroup.payments.length} payment{workspaceGroup.payments.length !== 1 ? 's' : ''}
                                      </Badge>
                                    </div>
                                  </CardHeader>
                                  <CardBody className="p-0">
                                    <Table responsive hover className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>Date</th>
                                          <th>Customer</th>
                                          <th>Plan/Type</th>
                                          <th>Amount</th>
                                          <th>Status</th>
                                          <th>Credits</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {workspaceGroup.payments.map(payment => (
                                          <tr key={payment._id}>
                                            <td>
                                              <div>
                                                {payment['paidAt'] ? new Date(payment['paidAt']).toLocaleDateString() :
                                                  payment['createdAt'] ? new Date(payment['createdAt']).toLocaleDateString() : 'N/A'}
                                                <br />
                                                <small className="text-muted">
                                                  {payment['paidAt'] ? new Date(payment['paidAt']).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                                    payment['createdAt'] ? new Date(payment['createdAt']).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                </small>
                                              </div>
                                            </td>
                                            <td>
                                              <div>
                                                <strong>
                                                  {payment['userId'] ?
                                                    `${payment['userId']['firstName'] || ''} ${payment['userId']['lastName'] || ''}`.trim() || 'Unknown User'
                                                    : 'N/A'}
                                                </strong>
                                                <br />
                                                <small className="text-muted">
                                                  {payment['userId']?.['email'] || 'No email'}
                                                </small>
                                              </div>
                                            </td>
                                            <td>
                                              <div>
                                                {getPaymentTypeBadge(payment['type'])}
                                                {payment['planId'] && (
                                                  <div>
                                                    <small className="text-muted">
                                                      {payment['planId']['displayName'] || payment['planId']['name']}
                                                    </small>
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                            <td>
                                              <strong>{formatCurrency(payment['amount'], payment['currency'])}</strong>
                                              {payment['planId']?.['interval'] && payment['type'] === 'subscription' && (
                                                <div>
                                                  <small className="text-muted">/{payment['planId']['interval']}</small>
                                                </div>
                                              )}
                                            </td>
                                            <td>{getPaymentStatusBadge(payment['status'])}</td>
                                            <td>
                                              {payment['type'] === 'credits' && payment['creditsInfo'] ? (
                                                <div>
                                                  <Badge color="info">
                                                    {payment['creditsInfo']['totalCredits']} credits
                                                  </Badge>
                                                  {payment['creditsInfo']['bonusCredits'] > 0 && (
                                                    <div>
                                                      <small className="text-success">
                                                        +{payment['creditsInfo']['bonusCredits']} bonus
                                                      </small>
                                                    </div>
                                                  )}
                                                </div>
                                              ) : '-'}
                                            </td>
                                            <td>
                                              <div className='action-box'>
                                                <button
                                                  size="sm"
                                                  color="primary"
                                                  className='action-btn'
                                                  outline
                                                  onClick={() => {
                                                    setSelectedPayment(payment);
                                                    setModalType('viewPayment');
                                                    setShowModal(true);
                                                  }}
                                                >
                                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43"></circle><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43"></path><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43"></path></svg></i>
                                                </button>
                                              </div>

                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </CardBody>
                                </Card>
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        // Regular Table View
                        <>
                          <Table responsive hover>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Workspace</th>
                                <th>Plan/Type</th>
                                <th>Amount</th>
                                <th>Status</th>
                                {!isAgencyUser() && <th>Credits</th>}
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payments.map(payment => (
                                <tr key={payment['_id']}>
                                  <td>
                                    <div>
                                      {payment['paidAt'] ? new Date(payment['paidAt']).toLocaleDateString() :
                                        payment['createdAt'] ? new Date(payment['createdAt']).toLocaleDateString() : 'N/A'}
                                      <br />
                                      <small className="text-muted">
                                        {payment['paidAt'] ? new Date(payment['paidAt']).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                          payment['createdAt'] ? new Date(payment['createdAt']).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <strong>
                                        {payment['userId'] ?
                                          `${payment['userId']['firstName'] || ''} ${payment['userId']['lastName'] || ''}`.trim() || 'Unknown User'
                                          : 'N/A'}
                                      </strong>
                                      <br />
                                      <small className="text-muted">
                                        {payment['userId']?.['email'] || 'No email'}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      {payment['workspaceId']?.['name'] || 'N/A'}
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      {getPaymentTypeBadge(payment['type'])}
                                      {payment['planId'] && (
                                        <div>
                                          <small className="text-muted">
                                            {formatPlanName(payment['planId']['displayName'] || payment['planId']['name'])}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <strong>{formatCurrency(payment['amount'], payment['currency'])}</strong>
                                    {payment['planId']?.['interval'] && payment['type'] === 'subscription' && (
                                      <div>
                                        <small className="text-muted">/{payment['planId']['interval']}</small>
                                      </div>
                                    )}
                                  </td>
                                  <td>{getPaymentStatusBadge(payment['status'])}</td>
                                  {!isAgencyUser() && (
                                    <td>
                                      {payment['type'] === 'credits' && payment['creditsInfo'] ? (
                                        <div>
                                          <Badge color="info">
                                            {payment['creditsInfo']['totalCredits']} credits
                                          </Badge>
                                          {payment['creditsInfo']['bonusCredits'] > 0 && (
                                            <div>
                                              <small className="text-success">
                                                +{payment['creditsInfo']['bonusCredits']} bonus
                                              </small>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  )}
                                  <td>
                                    <div className='action-box'>
                                      <button
                                        className='action-btn'
                                        title="View Details"
                                        onClick={() => {
                                          setSelectedPayment(payment);
                                          openModal('viewPayment', payment);
                                        }}
                                      >
                                        <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43"></circle><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43"></path><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43"></path></svg></i>
                                      </button>

                                      {/* REFUND BUTTON - COMMENTED OUT */}
                                      {/* {payment['status'] === 'succeeded' && payment['type'] !== 'refund' && (
                                        <button
                                          className='action-btn text-warning'
                                          title="Process Refund"
                                          onClick={() => handlePaymentRefund(payment)}
                                        >
                                          <i className="fa fa-undo"></i>
                                        </button>
                                      )} */}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {/* Pagination */}
                          {paymentsPagination && paymentsPagination.pages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="text-muted">
                                Showing {((paymentCurrentPage - 1) * paymentsPagination.limit) + 1} to {Math.min(paymentCurrentPage * paymentsPagination.limit, paymentsPagination.total)} of {paymentsPagination.total} payments
                              </div>
                              <Pagination>
                                <PaginationItem disabled={paymentCurrentPage === 1}>
                                  <PaginationLink
                                    previous
                                    onClick={() => setPaymentCurrentPage(paymentCurrentPage - 1)}
                                  />
                                </PaginationItem>

                                {[...Array(Math.min(5, paymentsPagination?.pages || 1))].map((_, i) => {
                                  const page = i + 1;
                                  return (
                                    <PaginationItem key={page} active={page === paymentCurrentPage}>
                                      <PaginationLink onClick={() => setPaymentCurrentPage(page)}>
                                        {page}
                                      </PaginationLink>
                                    </PaginationItem>
                                  );
                                })}

                                <PaginationItem disabled={paymentCurrentPage === (paymentsPagination?.pages || 1)}>
                                  <PaginationLink
                                    next
                                    onClick={() => setPaymentCurrentPage(paymentCurrentPage + 1)}
                                  />
                                </PaginationItem>
                              </Pagination>
                            </div>
                          )}
                        </>
                      )
                    ) : (
                      <div className="text-center py-5 d-block">
                        <i className="fa fa-credit-card fa-3x text-muted mb-3"></i>
                        <h5>No Payment History</h5>
                        <p className="text-muted">There are no payments to display yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Analytics Tab */}
            <TabPane tabId="analytics">
              <div className='content-dashboard'>
                <div className='title-area style-workshop mb-4'>
                  <div className='d-flex justify-content-between align-items-center w-100'>
                    <div>
                      <h4>Billing Overview</h4>
                      <p className="text-muted mb-0">Complete overview and detailed analytics of your billing, revenue, and subscription metrics</p>
                    </div>
                    <div className='d-flex gap-2'>
                      <select
                        className="form-control form-control-sm mr-2"
                        style={{ width: '150px', minHeight: '35px' }}
                        value={analyticsDateRange}
                        onChange={(e) => {
                          const newRange = e.target.value;
                          setAnalyticsDateRange(newRange);

                          // Calculate date range based on selection
                          let startDate, endDate = moment().endOf('day').toDate();
                          switch (newRange) {
                            case '7d':
                              startDate = moment().subtract(7, 'days').startOf('day').toDate();
                              break;
                            case '30d':
                              startDate = moment().subtract(30, 'days').startOf('day').toDate();
                              break;
                            case '3m':
                              startDate = moment().subtract(3, 'months').startOf('day').toDate();
                              break;
                            case '6m':
                              startDate = moment().subtract(6, 'months').startOf('day').toDate();
                              break;
                            case '12m':
                              startDate = moment().subtract(12, 'months').startOf('day').toDate();
                              break;
                            default:
                              startDate = moment().subtract(30, 'days').startOf('day').toDate();
                          }

                          // Reload analytics with new date range (same as overview tab)
                          dispatch(getRevenueAnalytics({ period: newRange, startDate, endDate }));
                          dispatch(getMRRAnalytics({ startDate, endDate }));
                          dispatch(getSubscriptionStats({ startDate, endDate }));
                          dispatch(getPlansOverview({ startDate, endDate }));
                        }}
                      >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="3m">Last 3 months</option>
                        <option value="6m">Last 6 months</option>
                        <option value="12m">Last 12 months</option>
                      </select>
                      <button size="sm" color="primary" className="btn-style-new" onClick={handleAnalyticsExport}>
                        <i className="fa fa-download mr-2"></i>Export Report
                      </button>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Cards */}
                <div className="mb-4">
                  <div className='counter-sections mb-3'>
                    <div className='counter-box style-two'>
                      <div className='counter-start' >
                        <div className='left-text-area'>
                          <h5>Total Revenue</h5>
                          <div className='CountUp-box'>
                            <h3 className="text-success">${(calculateTotalRevenue()).toLocaleString()}</h3>
                          </div>
                          <small className='text-success'>
                            <i className='fa fa-arrow-up'></i> All time
                          </small>
                        </div>
                        <div className='right-icon'>
                          <span className='icon'><i className='fa fa-dollar-sign text-success'></i></span>
                        </div>
                      </div>
                    </div>

                    <div className='counter-box style-two'>
                      <div className='counter-start' >
                        <div className='left-text-area'>
                          <h5>Monthly Recurring</h5>
                          <div className='CountUp-box'>
                            <h3 className="text-primary">${(revenueAnalytics?.planBasedSubscriptionValue || 0).toLocaleString()}</h3>
                          </div>
                          <small className={revenueAnalytics?.growth > 0 ? 'text-success' : 'text-danger'}>
                            <i className={`fa fa-arrow-${revenueAnalytics?.growth > 0 ? 'up' : 'down'}`}></i> {Math.abs(revenueAnalytics?.growth || 0).toFixed(1)}%
                          </small>
                        </div>
                        <div className='right-icon'>
                          <span className='icon'><i className='fa fa-chart-line text-primary'></i></span>
                        </div>
                      </div>
                    </div>

                    <div className='counter-box style-two'>
                      <div className='counter-start' >
                        <div className='left-text-area'>
                          <h5>Active Subscriptions</h5>
                          <div className='CountUp-box'>
                            <h3 className="" style={{ color: '#EF4D56' }}>{subscriptionStats?.activeCount || 0}</h3>
                          </div>
                          <small className='text-info'>
                            <i className='fa fa-users'></i> Active workspaces
                          </small>
                        </div>
                        <div className='right-icon'>
                          <span className='icon'><i className='fa fa-users text-info'></i></span>
                        </div>
                      </div>
                    </div>

                    <div className='counter-box style-two'>
                      <div className='counter-start' >
                        <div className='left-text-area'>
                          <h5>Avg Revenue/User</h5>
                          <div className='CountUp-box'>
                            <h3 className="" style={{ color: '#9333EA' }}>
                              ${calculateARPU()}
                            </h3>
                          </div>
                          <small className='text-warning'>
                            <i className='fa fa-user'></i> ARPU
                          </small>
                        </div>
                        <div className='right-icon'>
                          <span className='icon'><i className='fa fa-user-tag text-warning' style={{ fontSize: '18px' }}></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='chart-box'>
                  {/* Charts Row 1 - Plan Distribution and Customer Metrics */}
                  <div className='row'>
                    <div className='col-lg-6 col-md-12'>
                      <div className='chart-area'>
                        <h4>User Distribution by Plan</h4>
                        <p>Active subscribers across all plans</p>
                        <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getPlanDistributionChartData() ? (
                            <Doughnut
                              key={`analytics-plan-${analyticsDateRange}-${JSON.stringify(getPlanDistributionChartData().data || [])}`}
                              data={{
                                labels: getPlanDistributionChartData().labels,
                                datasets: [{
                                  data: getPlanDistributionChartData().data.length > 0 && getPlanDistributionChartData().data.every(d => d === 0)
                                    ? [1, 1, 1, 1] // Show equal distribution if all are 0
                                    : getPlanDistributionChartData().data,
                                  backgroundColor: getPlanDistributionChartData().colors,
                                  borderWidth: 2,
                                  borderColor: 'var(--sa-dark-surface)'
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'right',
                                    labels: {
                                      padding: 15,
                                      usePointStyle: true,
                                      font: { size: 12 },
                                      color: '#d4c0c8'
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: function (context) {
                                        const label = context.label || '';
                                        const chartData = getPlanDistributionChartData();
                                        const actualValue = chartData?.data[context.dataIndex] || 0;
                                        const total = chartData?.data.reduce((a, b) => a + b, 0) || 0;
                                        const percentage = total > 0 ? ((actualValue / total) * 100).toFixed(1) : '0';
                                        return `${label}: ${actualValue} users (${percentage}%)`;
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          ) : (
                            <div className='text-center text-muted'>
                              <i className='fa fa-chart-pie fa-3x mb-3'></i>
                              <p>No plan data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='col-lg-6 col-md-12'>
                      <Card>
                        <CardHeader className='bg-white'>
                          <h5 className='mb-0'>Customer Metrics</h5>
                          <small className='text-muted'>Key customer value indicators</small>
                        </CardHeader>
                        <CardBody>
                          <Row>
                            <Col xs={6} className='mb-3'>
                              <div className='metric-item'>
                                <label className='text-muted small'>Avg. Revenue per User</label>
                                <h4>${calculateARPU()}</h4>
                              </div>
                            </Col>
                            <Col xs={6} className='mb-3'>
                              <div className='metric-item'>
                                <label className='text-muted small'>Trial Conversion Rate</label>
                                <h4>{(subscriptionStats?.trialConversionRate || 0).toFixed(1)}%</h4>
                                <Progress value={subscriptionStats?.trialConversionRate || 0} color="primary" className='mt-2' />
                              </div>
                            </Col>
                            <Col xs={6} className='mb-3'>
                              <div className='metric-item'>
                                <label className='text-muted small'>Payment Success Rate</label>
                                <h4>{(subscriptionStats?.paymentSuccessRate || 0).toFixed(1)}%</h4>
                                <Progress value={subscriptionStats?.paymentSuccessRate || 0} color="success" className='mt-2' />
                              </div>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </div>
                    {/* Charts Row 1 - Plan Distribution */}
                    <div className='col-12'>
                      <div className='chart-area'>
                        <h4>Revenue Trend</h4>
                        <p>Monthly revenue over time</p>
                        <div style={{ height: '300px' }}>
                          {revenueAnalytics?.chartData ? (
                            <Line
                              key={`analytics-revenue-${analyticsDateRange}-${JSON.stringify(revenueAnalytics.chartData.labels || [])}`}
                              data={{
                                labels: revenueAnalytics.chartData.labels || [],
                                datasets: [
                                  {
                                    label: 'Subscription Revenue',
                                    data: revenueAnalytics.chartData.subscriptionRevenue || [],
                                    borderColor: '#2563EB',
                                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                                    fill: true,
                                    tension: 0.4
                                  },
                                  {
                                    label: 'Credits Revenue',
                                    data: revenueAnalytics.chartData.creditRevenue || [],
                                    borderColor: '#DF9524',
                                    backgroundColor: 'rgba(223, 149, 36, 0.1)',
                                    fill: true,
                                    tension: 0.4
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                  intersect: false,
                                  mode: 'index'
                                },
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    labels: {
                                      usePointStyle: true,
                                      padding: 20,
                                      color: '#d4c0c8'
                                    }
                                  }
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      color: '#d4c0c8',
                                      callback: function (value) {
                                        return '$' + value.toLocaleString();
                                      }
                                    },
                                    grid: { color: 'rgba(212,192,200,0.1)' }
                                  },
                                  x: {
                                    ticks: { color: '#d4c0c8' },
                                    grid: { color: 'rgba(212,192,200,0.1)' }
                                  }
                                }
                              }}
                            />
                          ) : (
                            <div className='text-center text-muted'>
                              <i className='fa fa-chart-line fa-3x mb-3'></i>
                              <p>Loading revenue trends...</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Payment Plans Tab */}
            <TabPane tabId="plans">
              <Plans
                accessType={accessType}
                isAgencyView={isAgencyUser()}
                workspaceId={workspaceId || currentWorkspace?._id}
              />
            </TabPane>

            {/* Calls Analytics Tab */}
            <TabPane tabId="calls-analytics">
              {activeTab === 'calls-analytics' && (
                <CallsAnalytics
                  accessType={accessType}
                  workspaceId={workspaceId || currentWorkspace?._id}
                />
              )}
            </TabPane>

            {/* Providers Tab */}
            <TabPane tabId="providers">
              <Providers />
            </TabPane>

            {/* Credit Management Tab - Only for non-agency users */}
            {!isAgencyUser() && (
              <TabPane tabId="credits">
                <CreditManagement dispatch={dispatch} />
              </TabPane>
            )}

          </TabContent>
        </div>

        {/* Action Modals */}
        <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
          <ModalHeader toggle={() => setShowModal(false)}>
            <h3>{modalType === 'view' && 'Subscription Details'}
              {modalType === 'credit' && 'Apply Credit'}
              {modalType === 'cancel' && 'Cancel Subscription'}
              {/* {modalType === 'refund' && 'Process Refund'} */}
              {modalType === 'viewPayment' && 'Payment Details'}
            </h3>
          </ModalHeader>
          <ModalBody>
            <Form className="dashboard-form mt-4">
              {modalType === 'viewPayment' && selectedPayment && (
                <div>
                  <Row>
                    <Col md={12}>
                      <div className="mb-3 p-3 bg-light rounded">
                        <h5 className="mb-3">Payment Details</h5>
                        <Row>
                          <Col md={6}>
                            <p><strong>Transaction ID:</strong> <code>{selectedPayment['_id']}</code></p>
                            <p><strong>Date:</strong> {selectedPayment['paidAt'] ? new Date(selectedPayment['paidAt']).toLocaleString() :
                              selectedPayment['createdAt'] ? new Date(selectedPayment['createdAt']).toLocaleString() : 'N/A'}</p>
                            <p><strong>Type:</strong> {getPaymentTypeBadge(selectedPayment['type'])}</p>
                            <p><strong>Status:</strong> {getPaymentStatusBadge(selectedPayment['status'])}</p>
                          </Col>
                          <Col md={6}>
                            <p><strong>Amount:</strong> {formatCurrency(selectedPayment['amount'], selectedPayment['currency'])}</p>
                            {selectedPayment['planId'] && (
                              <p><strong>Plan:</strong> {formatPlanName(selectedPayment['planId']['displayName'] || selectedPayment['planId']['name'])}
                                {selectedPayment['planId']['interval'] && ` (${selectedPayment['planId']['interval']})`}
                              </p>
                            )}
                            {selectedPayment['type'] === 'credits' && selectedPayment['creditsInfo'] && (
                              <>
                                <p><strong>Credits Purchased:</strong> {selectedPayment['creditsInfo']['creditsPurchased']}</p>
                                {selectedPayment['creditsInfo']['bonusCredits'] && (
                                  <>
                                    <p><strong>Bonus Credits:</strong> +{selectedPayment['creditsInfo']['bonusCredits']}</p>
                                    <p><strong>Total Credits:</strong> {selectedPayment['creditsInfo']['totalCredits']}</p>
                                  </>
                                )}
                              </>
                            )}
                          </Col>
                        </Row>
                      </div>

                      <div className="mb-3 p-3 bg-light rounded">
                        <h5 className="mb-3">Customer & Workspace</h5>
                        <Row>
                          <Col md={6}>
                            <p><strong>Customer:</strong> {selectedPayment['userId'] ?
                              `${selectedPayment['userId']['firstName'] || ''} ${selectedPayment['userId']['lastName'] || ''}`.trim() || 'Unknown'
                              : 'N/A'}</p>
                            <p><strong>Email:</strong> {selectedPayment['userId']?.['email'] || 'N/A'}</p>
                          </Col>
                          <Col md={6}>
                            <p><strong>Workspace:</strong> {selectedPayment['workspaceId']?.['name'] || 'N/A'}</p>
                            {selectedPayment['subscriptionId'] && (
                              <p><strong>Subscription Status:</strong> {getStatusBadge(selectedPayment['subscriptionId']?.['status'] || 'N/A', selectedPayment['subscriptionId'])}</p>
                            )}
                          </Col>
                        </Row>
                      </div>

                      {selectedPayment['stripeChargeId'] && (
                        <div className="mb-3 p-3 bg-light rounded">
                          <h5 className="mb-3">Payment Processing</h5>
                          <p><strong>Stripe Charge ID:</strong> <code>{selectedPayment['stripeChargeId']}</code></p>
                          {selectedPayment['stripePaymentIntentId'] && (
                            <p><strong>Payment Intent:</strong> <code>{selectedPayment['stripePaymentIntentId']}</code></p>
                          )}
                        </div>
                      )}

                      {selectedPayment['description'] && (
                        <div className="mb-3 p-3 bg-light rounded">
                          <h5 className="mb-3">Description</h5>
                          <p>{selectedPayment['description']}</p>
                        </div>
                      )}

                      {selectedPayment['failureReason'] && (
                        <div className="alert alert-danger">
                          <strong>Failure Reason:</strong> {selectedPayment['failureReason']}
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {modalType === 'view' && selectedSubscription && (
                <div>
                  <Row>
                    <Col md={6}>
                      <p><strong>Customer:</strong> {selectedSubscription['customerName']}</p>
                      <p><strong>Email:</strong> {selectedSubscription['customerEmail']}</p>
                      <p><strong>Workspace:</strong> {selectedSubscription['workspaceId']?.['name']}</p>
                      <p><strong>Plan:</strong> {formatPlanName(selectedSubscription['planName'])} ({selectedSubscription['planInterval']})</p>
                      <p><strong>Status:</strong> {getStatusBadge(selectedSubscription['status'], selectedSubscription)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Amount:</strong> ${selectedSubscription['amount']}/{selectedSubscription['planInterval']}</p>
                      <p><strong>Credit Balance:</strong> ${((selectedSubscription?.['creditBalance'] || 0) / 100).toFixed(2)}</p>
                      <p><strong>Created:</strong> {new Date(selectedSubscription['createdAt']).toLocaleDateString()}</p>
                      <p><strong>Next Billing:</strong> {selectedSubscription['nextBilling'] ? new Date(selectedSubscription['nextBilling']).toLocaleDateString() : 'N/A'}</p>
                      {selectedSubscription['stripeCustomerId'] && (
                        <p><strong>Stripe ID:</strong> <small>{selectedSubscription['stripeCustomerId']}</small></p>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {modalType === 'credit' && (
                <div>
                  {selectedSubscription ? (
                    <div className="mb-3 p-3 bg-light rounded">
                      <Row>
                        <Col md={6}>
                          <small className="text-muted">Customer</small>
                          <p className="mb-0"><strong>{selectedSubscription['customerName']}</strong></p>
                          <small>{selectedSubscription['customerEmail']}</small>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">Current Credit Balance</small>
                          <h4 className="mb-0 text-success">${((selectedSubscription?.['creditBalance'] || 0) / 100).toFixed(2)}</h4>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      <strong>Please select a subscription from the table</strong><br />
                      Click the green dollar sign ($) button next to any subscription to apply credits.
                    </div>
                  )}
                  {selectedSubscription && (
                    <Form>
                      <FormGroup>
                        <Label for="amount">Credit Amount to Add ($)</Label>
                        <InputGroup>
                          <InputGroupText>$</InputGroupText>
                          <Input
                            type="number"
                            id="amount"
                            min="0.01"
                            step="0.01"
                            placeholder="0.00"
                            value={formData['amount'] || ''}
                            onChange={(e) => setFormData({ ...formData, amount: e['target']['value'] })}
                          />
                        </InputGroup>
                        <small className="text-muted">
                          This will add ${formData['amount'] || '0.00'} to the customer's credit balance
                          {selectedSubscription?.['stripeCustomerId'] && ' and sync with Stripe'}
                        </small>
                      </FormGroup>
                      <FormGroup>
                        <Label for="description">Description/Reason</Label>
                        <Input
                          type="textarea"
                          id="description"
                          rows="3"
                          placeholder="Enter reason for applying credit..."
                          value={formData['description'] || ''}
                          onChange={(e) => setFormData({ ...formData, description: e['target']['value'] })}
                        />
                      </FormGroup>
                      {formData['amount'] && (
                        <div className="alert alert-info">
                          <strong>Summary:</strong><br />
                          Current Balance: ${((selectedSubscription?.['creditBalance'] || 0) / 100).toFixed(2)}<br />
                          Credit to Add: ${parseFloat(formData['amount']).toFixed(2)}<br />
                          <strong>New Balance: ${(((selectedSubscription?.['creditBalance'] || 0) / 100) + parseFloat(formData['amount'] || 0)).toFixed(2)}</strong>
                        </div>
                      )}
                    </Form>
                  )}
                </div>
              )}

              {modalType === 'cancel' && (
                <Form>
                  <p>Are you sure you want to cancel this subscription?</p>
                  <FormGroup>
                    <Label for="reason">Cancellation Reason</Label>
                    <Input
                      type="textarea"
                      id="reason"
                      value={formData['reason'] || ''}
                      onChange={(e) => setFormData({ ...formData, reason: e['target']['value'] })}
                    />
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={formData['immediate'] || false}
                        onChange={(e) => setFormData({ ...formData, immediate: e['target']['checked'] })}
                      />
                      Cancel immediately (otherwise will cancel at period end)
                    </Label>
                  </FormGroup>
                </Form>
              )}

              {/* REFUND MODAL CONTENT - COMMENTED OUT */}
              {/* {modalType === 'refund' && selectedPayment && (
                <div>
                  {selectedPayment['status'] === 'refunded' ? (
                    <div className="alert alert-warning">
                      <strong>This payment has already been refunded.</strong>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 p-3 bg-light rounded">
                        <Row>
                          <Col md={6}>
                            <small className="text-muted">Original Payment</small>
                            <h4 className="mb-0">{formatCurrency(selectedPayment['amount'], selectedPayment['currency'])}</h4>
                            <small>{selectedPayment['invoiceNumber']}</small>
                          </Col>
                          <Col md={6}>
                            <small className="text-muted">Customer</small>
                            <p className="mb-0"><strong>{selectedPayment['userId']?.['firstName']} {selectedPayment['userId']?.['lastName']}</strong></p>
                            <small>{selectedPayment['userId']?.['email']}</small>
                          </Col>
                        </Row>
                      </div>

                      <Form>
                        <FormGroup>
                          <Label for="refundAmount">Refund Amount</Label>
                          <InputGroup>
                            <InputGroupText>{selectedPayment['currency'] === 'EUR' ? '€' : selectedPayment['currency'] === 'GBP' ? '£' : '$'}</InputGroupText>
                            <Input
                              type="number"
                              id="refundAmount"
                              min="0.01"
                              max={selectedPayment['amount']}
                              step="0.01"
                              placeholder="0.00"
                              value={formData['amount'] || selectedPayment['amount']}
                              onChange={(e) => setFormData({ ...formData, amount: e['target']['value'] })}
                            />
                          </InputGroup>
                          <small className="text-muted">
                            Maximum refundable amount: {formatCurrency(selectedPayment['amount'], selectedPayment['currency'])}
                          </small>
                        </FormGroup>

                        <FormGroup>
                          <Label for="refundReason">Refund Reason <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            id="refundReason"
                            value={formData['reason'] || ''}
                            onChange={(e) => setFormData({ ...formData, reason: e['target']['value'] })}
                            required
                          >
                            <option value="">Select a reason...</option>
                            <option value="duplicate">Duplicate payment</option>
                            <option value="fraudulent">Fraudulent payment</option>
                            <option value="requested_by_customer">Customer requested</option>
                            <option value="service_issue">Service issue</option>
                            <option value="other">Other</option>
                          </Input>
                        </FormGroup>

                        {formData['reason'] === 'other' && (
                          <FormGroup>
                            <Label for="refundNotes">Additional Notes</Label>
                            <Input
                              type="textarea"
                              id="refundNotes"
                              rows="3"
                              placeholder="Please provide additional details..."
                              value={formData['notes'] || ''}
                              onChange={(e) => setFormData({ ...formData, notes: e['target']['value'] })}
                            />
                          </FormGroup>
                        )}

                        {formData['amount'] && parseFloat(formData['amount']) > 0 && (
                          <div className="alert alert-info">
                            <strong>Refund Summary:</strong><br />
                            Amount to refund: {formatCurrency(parseFloat(formData['amount']), selectedPayment['currency'])}<br />
                            {parseFloat(formData['amount']) < selectedPayment['amount'] && (
                              <>Partial refund ({((parseFloat(formData['amount']) / selectedPayment['amount']) * 100).toFixed(1)}% of original payment)</>
                            )}
                            {parseFloat(formData['amount']) === selectedPayment['amount'] && (
                              <>Full refund</>
                            )}
                          </div>
                        )}

                        <div className="alert alert-warning">
                          <i className="fa fa-exclamation-triangle mr-2"></i>
                          <strong>Important:</strong> This action cannot be undone. The refund will be processed through Stripe and the customer will receive their money back.
                        </div>
                      </Form>
                    </>
                  )}
                </div>
              )} */}
            </Form>
          </ModalBody>
          <ModalFooter>
            <button className='btn-style-border' onClick={() => setShowModal(false)}>
              Close
            </button>

            {modalType === 'credit' && selectedSubscription && (
              <button
                className='btn-style-new'
                onClick={() => {
                  if (selectedSubscription?.['_id']) {
                    handleSubscriptionAction(selectedSubscription['_id'], 'credit', formData);
                    setShowModal(false);
                  }
                }}
                disabled={!formData['amount'] || parseFloat(formData['amount']) <= 0}
              >
                Apply Credit
              </button>
            )}
            {modalType === 'cancel' && selectedSubscription && (
              <button
                className='btn-style-border'
                onClick={() => {
                  if (selectedSubscription?.['_id']) {
                    handleSubscriptionAction(selectedSubscription['_id'], 'cancel', formData);
                    setShowModal(false);
                  }
                }}
              >
                Cancel Subscription
              </button>
            )}
            {/* REFUND BUTTON IN MODAL FOOTER - COMMENTED OUT */}
            {/* {modalType === 'refund' && selectedPayment && (
              <button
                className='btn-style-new'
                onClick={processPaymentRefund}
                disabled={!formData['amount'] || parseFloat(formData['amount']) <= 0 || parseFloat(formData['amount']) > selectedPayment['amount'] || !formData['reason'] || selectedPayment['status'] === 'refunded'}
              >
                Process Refund
              </button>
            )} */}
          </ModalFooter>
        </Modal>

        {/* Assign Plan Modal */}
        <Modal isOpen={showAssignPlanModal} toggle={() => setShowAssignPlanModal(false)} size="xl" className={`main-modal modal-dialog-centered`}>
          <ModalHeader toggle={() => setShowAssignPlanModal(false)}>
            Assign Plan to Workspaces
          </ModalHeader>
          <ModalBody>
            {selectedPlanToAssign && (
              <div className="mb-4 p-3 bg-light rounded">
                <h5>Selected Plan: {selectedPlanToAssign.displayName || selectedPlanToAssign.name}</h5>
                <div className="d-flex gap-4">
                  <span><strong>Type:</strong> {selectedPlanToAssign.type}</span>
                  <span><strong>Price:</strong> ${selectedPlanToAssign.price}{selectedPlanToAssign.interval ? `/${selectedPlanToAssign.interval}` : ''}</span>
                  {selectedPlanToAssign.features && (
                    <span><strong>Features:</strong> {selectedPlanToAssign.features.length} included</span>
                  )}
                </div>
              </div>
            )}

            <div className="mb-3">
              <Label>Search Workspaces (Optional)</Label>
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Type to filter workspaces by name or owner email..."
                  value={workspaceSearchTerm}
                  onChange={handleWorkspaceSearch}
                />
                <InputGroupText style={{ cursor: 'pointer' }}>
                  {workspaceSearchTerm ? (
                    <i className="fa fa-times text-danger"
                      onClick={() => {
                        setWorkspaceSearchTerm('');
                        loadAllWorkspaces();
                      }}
                      title="Clear search"></i>
                  ) : (
                    <i className="fa fa-search"></i>
                  )}
                </InputGroupText>
              </InputGroup>
              <small className="text-muted">
                All workspaces are shown below ({availableWorkspaces.length} total). Use the search bar to filter if needed.
              </small>
            </div>

            {selectedWorkspaces.length > 0 && (
              <div className="mb-3 p-3 bg-info bg-opacity-10 rounded">
                <h6><i className="fa fa-info-circle mr-2"></i>Selection Summary</h6>
                <p className="mb-1">
                  <strong>{selectedWorkspaces.length} workspaces selected</strong> for plan assignment
                </p>
                <p className="mb-0">
                  Estimated monthly value: <strong>${(selectedPlanToAssign?.price * selectedWorkspaces.length || 0).toLocaleString()}</strong>
                </p>
              </div>
            )}

            <div className="workspace-selection-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {loadingWorkspaces ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading workspaces...</span>
                  </div>
                </div>
              ) : availableWorkspaces.length > 0 ? (
                <Table hover>
                  <thead>
                    <tr>
                      <th width="40">
                        <Input
                          type="checkbox"
                          checked={selectAllWorkspaces && availableWorkspaces.length > 0}
                          onChange={(e) => handleSelectAllWorkspaces(e.target.checked)}
                        />
                      </th>
                      <th>Workspace</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th>Current Plan</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableWorkspaces.map(workspace => {
                      const isSelected = selectedWorkspaces.find(w => w._id === workspace._id);
                      return (
                        <tr key={workspace._id}
                          className={isSelected ? 'table-active' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleWorkspaceSelection(workspace, !isSelected)}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={(e) => handleWorkspaceSelection(workspace, e.target.checked)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {workspace.logo && (
                                <img src={workspace.logo} alt="" className="mr-2" style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                              )}
                              <div>
                                <strong>{workspace.name}</strong>
                                {workspace.description && (
                                  <div>
                                    <small className="text-muted">{workspace.description}</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            {workspace.createdBy ? (
                              <div>
                                <strong>{workspace.createdBy.firstName} {workspace.createdBy.lastName}</strong>
                                <div>
                                  <small className="text-muted">{workspace.createdBy.email}</small>
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            <Badge color={
                              workspace.status === 'active' ? 'success' :
                                workspace.status === 'trial' ? 'info' :
                                  workspace.status === 'suspended' ? 'warning' : 'secondary'
                            }>
                              {workspace.status}
                            </Badge>
                          </td>
                          <td>
                            {workspace.currentSubscription ? (
                              <Badge color="primary">{formatPlanName(workspace.currentSubscription.planName)}</Badge>
                            ) : (
                              <span className="text-muted">Free</span>
                            )}
                          </td>
                          <td>
                            <code>{workspace.id}</code>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-4 text-muted">
                  {workspaceSearchTerm ? 'No workspaces found matching your search' : 'No workspaces available'}
                </div>
              )}
            </div>

            {selectedWorkspaces.length > 0 && (
              <div className="mt-3">
                <div className="mb-3">
                  <h6>Selected Workspaces ({selectedWorkspaces.length})</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedWorkspaces.slice(0, 5).map(workspace => (
                      <Badge key={workspace._id} color="primary" className="p-2">
                        {workspace.name}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '10px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWorkspaceSelection(workspace, false);
                          }}
                        ></button>
                      </Badge>
                    ))}
                    {selectedWorkspaces.length > 5 && (
                      <Badge color="secondary" className="p-2">
                        +{selectedWorkspaces.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="alert alert-warning">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  <strong>Important:</strong> This will assign the <strong>{selectedPlanToAssign?.displayName || selectedPlanToAssign?.name}</strong> plan to all selected workspaces. Existing subscriptions will be replaced.
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowAssignPlanModal(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAssignPlan}
              disabled={selectedWorkspaces.length === 0 || assigningPlan}
            >
              <i className="fa fa-check mr-2"></i>
              Assign to {selectedWorkspaces.length} Workspace{selectedWorkspaces.length !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Progress Modal */}
        <Modal isOpen={showProgressModal} centered backdrop="static" keyboard={false}>
          <ModalHeader>
            Assignment Progress
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Progress: {assignmentProgress.current} of {assignmentProgress.total}</span>
                <span>{assignmentProgress.total > 0 ? Math.round((assignmentProgress.current / assignmentProgress.total) * 100) : 0}%</span>
              </div>
              <Progress
                value={assignmentProgress.total > 0 ? (assignmentProgress.current / assignmentProgress.total) * 100 : 0}
                color="primary"
              />
              <div className="mt-2 text-muted">
                {assignmentProgress.status}
              </div>
            </div>

            {assignmentResults.length > 0 && (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <h6>Results:</h6>
                {assignmentResults.map((result, index) => (
                  <div key={index} className={`alert ${result.success ? 'alert-success' : 'alert-danger'} py-2`}>
                    <div className="d-flex justify-content-between">
                      <strong>{result.workspace.name}</strong>
                      <small>
                        <i className={`fa ${result.success ? 'fa-check' : 'fa-times'}`}></i>
                      </small>
                    </div>
                    <small>{result.message}</small>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>
          {assignmentProgress.status === 'Completed' && (
            <ModalFooter>
              <Button color="primary" onClick={() => {
                setShowProgressModal(false);
                setAssignmentResults([]);
                setAssignmentProgress({ current: 0, total: 0, status: '' });
              }}>
                Close
              </Button>
            </ModalFooter>
          )}
        </Modal>
      </div>

      {/* Subscription Details Sidebar */}
      <SubDetailsSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        subscription={selectedSubscription}
        onRefresh={() => {
          dispatch(getAdminSubscriptions());
        }}
      />
    </>
  )
};

export default BillingManagement;
